/**
 * Visual status for jobs the user has already optimized or applied to.
 */

import { Check, Clock, Sparkles } from 'lucide-react';
import React from 'react';

import type { AppliedJobInfo } from '../hooks/useAppliedJobsMap';

export type JobWorkflowKind = 'applied' | 'optimized' | 'in_progress' | 'viewed';

export function resolveJobWorkflowKind(info: AppliedJobInfo): JobWorkflowKind {
  const isApplied =
    info.status === 'applied' ||
    info.status === 'interviewing' ||
    info.status === 'offered' ||
    info.status === 'accepted';
  if (isApplied) return 'applied';
  if (info.hasResume || info.hasCoverLetter) return 'optimized';
  if (info.status === 'in_progress') return 'in_progress';
  return 'viewed';
}

export function JobWorkflowStatusBadge({
  info,
  size = 'sm',
}: {
  info: AppliedJobInfo;
  size?: 'sm' | 'md';
}) {
  const kind = resolveJobWorkflowKind(info);
  const pad = size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]';

  if (kind === 'applied') {
    return (
      <span
        title={`Applied · updated ${new Date(info.lastUpdated).toLocaleDateString()}`}
        className={`agent-job-badge agent-job-badge--applied inline-flex items-center gap-1 rounded font-bold whitespace-nowrap shrink-0 ${pad}`}
      >
        <Check className="w-2.5 h-2.5" strokeWidth={3} />
        Applied
      </span>
    );
  }

  if (kind === 'optimized') {
    return (
      <span
        title={
          `Resume optimized` +
          (info.matchScore != null ? ` · ATS match ${info.matchScore}%` : '') +
          ` · ${new Date(info.lastUpdated).toLocaleDateString()}`
        }
        className={`agent-job-badge agent-job-badge--optimized inline-flex items-center gap-1 rounded font-bold whitespace-nowrap shrink-0 ${pad}`}
      >
        <Sparkles className="w-2.5 h-2.5" />
        Optimized
        {info.matchScore != null ? ` ${info.matchScore}%` : ''}
      </span>
    );
  }

  if (kind === 'in_progress') {
    return (
      <span
        title="Application in progress — finish on the company site"
        className={`inline-flex items-center gap-1 rounded font-semibold whitespace-nowrap shrink-0 ${pad}`}
        style={{ background: 'rgba(245,158,11,0.12)', color: '#b45309', border: '1px solid rgba(245,158,11,0.35)' }}
      >
        <Clock className="w-2.5 h-2.5" />
        Applying…
      </span>
    );
  }

  return (
    <span
      title="Previously viewed"
      className={`agent-job-badge agent-job-badge--viewed inline-flex items-center gap-1 rounded font-semibold whitespace-nowrap shrink-0 ${pad}`}
    >
      Viewed
    </span>
  );
}

export default JobWorkflowStatusBadge;
